"use client";

import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import useModalCloseOnBrowserButton from "@repo/shared-ui/useModalCloseOnBrowserButton";
import { FC, useEffect, useState } from "react";
import {
  getWatchList,
  updateWatchList,
} from "../../services/TradingMallService";
import showToast from "../../utils/toast/toastify";
import WatchListPortal from "./WatchListPortal";
import WatchlistRow from "./WatchlistRow";
import type {
  WatchlistModalProps,
  WatchlistWithMetadata,
} from "./types";

export const WatchlistModal: FC<WatchlistModalProps> = ({
  isOpen,
  onClose,
  selectedStock,
  errorMsg: defaultErrorMsg,
  successMsg,
  modalTitle,
  modalBtnCta,
  onWatchlistSelect,
}) => {
  const [watchListDetails, setWatchListDetails] = useState<
    WatchlistWithMetadata[]
  >([]);
  const [selectedWatchlist, setSelectedWatchlist] =
    useState<WatchlistWithMetadata | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  useModalCloseOnBrowserButton(isOpen, onClose);

  // Update parent when watchlist is selected
  const handleWatchlistSelect = (watchlist: WatchlistWithMetadata) => {
    setSelectedWatchlist(watchlist);
    // Get the full watchlist data with wlData
    const fullWatchlistData = watchListDetails.find(
      (wl) => wl.wlId === watchlist.wlId,
    );
    if (fullWatchlistData) {
      onWatchlistSelect?.(fullWatchlistData);
    }
  };

  /**
   * Fetch watchlists when modal opens
   */
  useEffect(() => {
    const fetchWatchlists = async () => {
      if (!isOpen) return;

      try {
        setLoading(true);
        setErrorMsg("");
        const response = await getWatchList("", 1, 20);

        if (
          response?.data?.watchlist &&
          Object.keys(response.data.watchlist).length > 0
        ) {
          const watchlists = Array.isArray(response.data.watchlist)
            ? response.data.watchlist
            : [response.data.watchlist];

          setWatchListDetails(watchlists);
        } else {
          setWatchListDetails([]);
        }
      } catch (error: any) {
        const errorMessage =
          error?.message || "Failed to load watchlists. Please try again.";
        setErrorMsg(errorMessage);
        setWatchListDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWatchlists();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedWatchlist(null);
      setErrorMsg("");
      onWatchlistSelect?.(null);
    }
  }, [isOpen, onWatchlistSelect]);

  const handleAddToWatchList = async () => {
    if (!selectedWatchlist?.wlId) {
      if (defaultErrorMsg) {
        setErrorMsg(defaultErrorMsg);
      }
      return;
    }

    if (!selectedStock) {
      setErrorMsg("Please select a stock to add to watchlist");
      return;
    }

    try {
      setErrorMsg("");
      setLoading(true);

      const existingStocks =
        watchListDetails
          ?.find((wl) => wl.wlId === selectedWatchlist?.wlId)
          ?.wlData?.map((item) => `${item.exchange}@${item.token}`) ?? [];

      const addedStock = `${selectedStock.exchange}@${selectedStock.token}`;
      const wlData = [...existingStocks, addedStock].join("|");

      const response = await updateWatchList(
        wlData,
        selectedWatchlist.wlId.toString(),
        selectedWatchlist.wlName,
      );

      if (!response?.data) {
        const errorMessage = response?.message;

        setErrorMsg(errorMessage);
        return;
      }

      if (successMsg) {
        showToast({ variant: "success", message: successMsg });
      }
      onClose();
    } catch (error: any) {
      setErrorMsg(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const renderWatchlistContent = () => {
    if (loading) {
      return <BFLSkeleton count={10} height={80} />;
    }

    if (watchListDetails.length === 0) {
      return (
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-600">No watchlists found</p>
        </div>
      );
    }

    return watchListDetails.map((watchlist) => (
      <WatchlistRow
        key={watchlist.wlId}
        watchlist={watchlist}
        isSelected={selectedWatchlist?.wlId === watchlist.wlId}
        onSelect={handleWatchlistSelect}
      />
    ));
  };

  return (
    <WatchListPortal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      buttonText={modalBtnCta}
      primaryButtonClassNames="!px-[8px]"
      errorMessage={errorMsg || undefined}
      onPrimaryButtonClick={handleAddToWatchList}
    >
      <div>
        {renderWatchlistContent()}
      </div>
    </WatchListPortal>
  );
};

export default WatchlistModal;
